/**
 * KillSwitchModal — modal wrapper around KillSwitchPanel.
 *
 * Opens as an overlay when the user clicks "Configure" in the kill switch status bar.
 * Click-outside and Escape key both close the modal.
 */

import { useEffect, useRef } from 'react';
import { KillSwitchPanel } from './KillSwitchPanel';

interface KillSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KillSwitchModal({ isOpen, onClose }: KillSwitchModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label="Kill Switch Settings"
    >
      <div
        ref={panelRef}
        className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-base font-semibold text-white">Kill Switch Settings</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          <KillSwitchPanel />
        </div>
      </div>
    </div>
  );
}
